import { isAxiosError } from 'axios';
import { posClient } from './client';
import {
  createOrderResponseSchema,
  normalizeCreatedOrder,
  normalizePaymentSummary,
  normalizeQuote,
  normalizeRound,
  quoteConflictSchema,
  quoteResponseSchema,
  type CompletedOrder,
  type CreateOrderPayload,
  type OrderQuote,
  type OrderRound,
  type OrderTypeWire,
  type OrderItemPayload,
  type PaymentSummary,
} from '@/types/checkout';

/**
 * Checkout adapter (V1-04, extended V2.2-01) — POST {pos}/orders and
 * POST {pos}/orders/quote.
 *
 * Contract:
 * - 201 `{ success, order, round?, payment? }` — `order` is the AUTHORITATIVE
 *   upstream order. Callers must replace every locally displayed amount with
 *   these values. `round` is the batch just accepted, for the kitchen ticket.
 * - 422 — standard Laravel `{ message, errors }`, keyed by field so the ticket
 *   can point at the empty box.
 * - 409 — pricing (`QUOTE_CHANGED`), operation reuse (`IDEMPOTENCY_CONFLICT`),
 *   or a business-state rejection such as a drawer frozen for counting.
 *   Only the named conflicts change the quote or operation recovery flow.
 * - 400 `{ success: false, error }` — business rejections (stock, branch
 *   closed, schedules). Shown verbatim.
 *
 * Transport rules (03-pos-shell.md P2):
 * - NO retry lives here or in any interceptor. Retrying is the CALLER's
 *   decision, and it is safe now only because the payload carries
 *   `client_order_id`: replaying a key returns the original order instead of
 *   creating a second one.
 * - NO AbortSignal on create, on purpose: cancelling a checkout mid-flight
 *   manufactures ambiguity. The request runs to completion or times out.
 * - Timeout 45s > the pos-api→main-app proxy's own 30s, so the server settles
 *   first whenever it can. A local timeout surfaces as ApiError status 0, which
 *   the store treats as ambiguous — cart and operation key both preserved.
 */
export const CHECKOUT_TIMEOUT_MS = 45_000;

/** Quoting sits on the critical path of opening tender, so it fails fast. */
export const QUOTE_TIMEOUT_MS = 12_000;

export interface QuoteRequest {
  order_type: OrderTypeWire;
  items: OrderItemPayload[];
  area_id?: number;
  /**
   * THE QUOTE HAS TO ASK THE SAME QUESTION THE CREATE ASKS.
   *
   * This endpoint exists so tender opens on the total the order will really be
   * written with, and `quote_fingerprint` makes the create REFUSE when the two
   * disagree (409 → QuoteChangedError below). So an input the create sends and
   * the quote does not is not a missing feature — it is a guaranteed conflict:
   * a retail delivery would be quoted with no postage, created with postage,
   * and the counter would meet a 409 on a ticket where nothing had changed.
   *
   * pos-api validates and forwards the key on both endpoints for exactly that
   * reason (OrderController.php:465 on quote, :97 on create).
   */
  shipping_method_id?: number;
  discount_amount?: number;
  promotion_codes?: string[];
  customer_id?: number;
  customer_phone?: string;
}

/**
 * The create refused because the price moved (409 `QUOTE_CHANGED`).
 *
 * Its own type because the cashier's next action is different: they are not
 * fixing a bad field, they are being shown a new total to confirm. No order,
 * stock movement or coupon use exists when this is thrown.
 */
export class QuoteChangedError extends Error {
  constructor(readonly quote: OrderQuote | null) {
    super('The total changed before this order was recorded. Confirm the new amount.');
    this.name = 'QuoteChangedError';
  }
}

/**
 * The operation id was already used for a DIFFERENT order (409).
 *
 * Retrying cannot help and must not be offered — the key is spent. The cart
 * mints a new one only after the cashier confirms what actually landed.
 */
export class OperationConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OperationConflictError';
  }
}

export interface CreatedSale {
  order: CompletedOrder;
  /** The accepted round, for the kitchen ticket. Null on a seated-empty tab. */
  round: OrderRound | null;
  /** Server-computed payment figures the receipt prints verbatim. */
  payment: PaymentSummary | null;
}

export const checkoutApi = {
  /**
   * Price a ticket without recording anything.
   *
   * Runs the SAME server calculation the create runs, so the number the cashier
   * tenders against is the number the order will carry — or the create refuses
   * with a fresh quote rather than quietly charging a different amount.
   */
  async quote(payload: QuoteRequest): Promise<OrderQuote> {
    const res = await posClient.post('/orders/quote', payload, { timeout: QUOTE_TIMEOUT_MS });
    return normalizeQuote(quoteResponseSchema.parse(res.data).quote);
  },

  async create(payload: CreateOrderPayload): Promise<CreatedSale> {
    try {
      const res = await posClient.post('/orders', payload, { timeout: CHECKOUT_TIMEOUT_MS });
      const body = createOrderResponseSchema.parse(res.data);
      return {
        order: normalizeCreatedOrder(body.order),
        round: body.round ? normalizeRound(body.round) : null,
        payment: body.payment ? normalizePaymentSummary(body.payment) : null,
      };
    } catch (e) {
      // Read the raw 409 body rather than the normalized error: normalizeApiError
      // flattens every envelope to a message, and the two conflicts are told
      // apart by `code` and carry a fresh quote the cashier has to see.
      if (isAxiosError(e) && e.response?.status === 409) {
        const conflict = quoteConflictSchema.safeParse(e.response.data);

        if (conflict.success && conflict.data.code === 'IDEMPOTENCY_CONFLICT') {
          throw new OperationConflictError(
            conflict.data.error ?? 'This operation id was already used for a different order.',
          );
        }

        if (conflict.success && conflict.data.code === 'QUOTE_CHANGED') {
          throw new QuoteChangedError(
            conflict.data.quote ? normalizeQuote(conflict.data.quote) : null,
          );
        }
      }

      throw e;
    }
  },
};
